import React, { createContext, useEffect, useCallback, useRef } from 'react'
import { useNoteProcessor } from './useNoteProcessor'
import {
  type AddContentInput,
  type RemoveContentInput,
} from './NoteProcessorProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { filenameToDate, Note } from '../utils/note'
import {
  usePrivateCalendarNotesByDate,
  useTeamCalendarNotesByDate,
} from '../hooks/useNotesFactory'
import { useSelectedDate } from '../contexts/SelectedDate'
import { useSelectedRecordName } from '../contexts/SelectedRecordName'
import { RepeatTaskEventDetail } from '@packages/blocknote-react'
import { BlockNoteEditor } from '@packages/blocknote-core'

/**
 * Constants and types for the repeat task system
 *
 * The repeat task system allows users to create tasks that repeat at regular intervals.
 * Tasks can repeat daily, weekly, monthly, quarterly, or yearly.
 *
 * The system uses a special syntax in task lines: @repeat(n/total)
 * Where 'n' is the current repeat number and 'total' is the total number of repeats.
 *
 * Example: "* Buy groceries @repeat(1/5)" - This is the first occurrence of a task
 * that will repeat 5 times total.
 */

// Constants for frequency calculations
const FREQUENCY = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
} as const

type Frequency = (typeof FREQUENCY)[keyof typeof FREQUENCY]

/**
 * Frequency thresholds in days
 *
 * These thresholds are used to infer the frequency of a repeating task
 * based on the number of days between occurrences:
 * - Week: Exactly 7 days
 * - Month: Between 28-31 days
 * - Quarter: Between 89-92 days
 * - Year: Between 364-366 days
 * - Day: Any other interval (default)
 */
const FREQUENCY_THRESHOLDS = {
  [FREQUENCY.WEEK]: { exact: 7 },
  [FREQUENCY.MONTH]: { min: 28, max: 31 },
  [FREQUENCY.QUARTER]: { min: 89, max: 92 },
  [FREQUENCY.YEAR]: { min: 364, max: 366 },
} as const

/**
 * Regular expression to match repeat task syntax
 *
 * Captures:
 * 1. Leading whitespace/indentation
 * 2. Task marker and task content
 * 3. Current repeat number
 * 4. Total number of repeats
 *
 * Example match: "  * Buy groceries @repeat(2/5)"
 * - Group 1: "  " (indentation)
 * - Group 2: "* Buy groceries " (task marker and content)
 * - Group 3: "2" (current repeat)
 * - Group 4: "5" (total repeats)
 */
const taskMarker = BlockNoteEditor.taskItemCharacters[0] // User configured
const markers = [taskMarker, '+']
const REPEAT_TASK_REGEX = new RegExp(
  `^(\\s*)([${markers.join('')}]\\s+.*?\\s+)@repeat\\((\\d+)\\/(\\d+)\\)`
)

/**
 * Infer the frequency of a repeating task based on the number of days between occurrences
 *
 * This function analyzes the day difference between two task occurrences and determines
 * the most likely frequency (daily, weekly, monthly, quarterly, or yearly).
 *
 * The function uses the FREQUENCY_THRESHOLDS constants to make this determination:
 * - Exactly 7 days → Weekly
 * - 28-31 days → Monthly
 * - 89-92 days → Quarterly
 * - 364-366 days → Yearly
 * - Any other interval → Daily (default)
 *
 * @param diff - The number of days between two task occurrences
 * @returns The inferred frequency (day, week, month, quarter, year)
 */
const inferFrequencyFromDayDiff = (diff: number): Frequency => {
  if (diff === FREQUENCY_THRESHOLDS[FREQUENCY.WEEK].exact) return FREQUENCY.WEEK
  if (
    diff >= FREQUENCY_THRESHOLDS[FREQUENCY.MONTH].min &&
    diff <= FREQUENCY_THRESHOLDS[FREQUENCY.MONTH].max
  )
    return FREQUENCY.MONTH
  if (
    diff >= FREQUENCY_THRESHOLDS[FREQUENCY.QUARTER].min &&
    diff <= FREQUENCY_THRESHOLDS[FREQUENCY.QUARTER].max
  )
    return FREQUENCY.QUARTER
  if (
    diff >= FREQUENCY_THRESHOLDS[FREQUENCY.YEAR].min &&
    diff <= FREQUENCY_THRESHOLDS[FREQUENCY.YEAR].max
  )
    return FREQUENCY.YEAR
  return FREQUENCY.DAY
}

/**
 * Calculate the next date for a repeating task based on frequency and steps
 *
 * This function computes the future date for a repeating task by adding the appropriate
 * time interval based on the frequency:
 * - Daily: Add N days
 * - Weekly: Add N weeks (7*N days)
 * - Monthly: Add N months
 * - Quarterly: Add N quarters (3*N months)
 * - Yearly: Add N years
 *
 * @param currentDate - The reference date to calculate from
 * @param steps - The number of frequency units to add
 * @param frequency - The frequency type (day, week, month, quarter, year)
 * @returns A new date object representing the future date
 */
const calculateNextDate = (
  currentDate: Dayjs,
  steps: number,
  frequency: Frequency
): Dayjs => {
  switch (frequency) {
    case FREQUENCY.WEEK: {
      return currentDate.add(steps * 7, 'day')
    }
    case FREQUENCY.MONTH: {
      return currentDate.add(steps, 'month')
    }
    case FREQUENCY.QUARTER: {
      return currentDate.add(steps * 3, 'month')
    }
    case FREQUENCY.YEAR: {
      return currentDate.add(steps, 'year')
    }
    default: {
      return currentDate.add(steps, 'day')
    }
  }
}

// Create a stable task identifier that normalizes whitespace
function createTaskIdentifier(taskBase: string, totalRepeats: number): string {
  // Normalize whitespace in the task base (which includes the bullet)
  const normalizedTaskBase = taskBase.trim().replaceAll(/\s+/g, ' ')
  return `${normalizedTaskBase}-${totalRepeats.toString()}`
}

export type RepeatTaskContextType = {
  checkForDeletedRepeatTasks: (
    oldContent: string,
    newContent: string,
    filename: string
  ) => void
}

type RepeatTaskProviderProps = {
  children: React.ReactNode
}

export const RepeatTaskContext = createContext<
  RepeatTaskContextType | undefined
>(undefined)

/**
 * RepeatTaskProvider - Manages repeating tasks across the application
 *
 * This provider is responsible for:
 * 1. Tracking and managing repeating tasks across different notes
 * 2. Handling the creation of future task instances when a repeat task is created
 * 3. Handling the deletion of future task instances when a repeat task is deleted
 * 4. Inferring the frequency of repeating tasks based on their occurrence pattern
 *
 * The provider uses the NoteProcessor to add/remove content in calendar notes and
 * maintains a frequency map to remember the frequency of each repeating task.
 *
 * @param children - React children components
 */
export function RepeatTaskProvider({ children }: RepeatTaskProviderProps) {
  // Maps to store the frequency of each repeating task (persists across renders)
  // We need separate maps for private notes and each team space
  // The main map is a map of maps, where the key is the teamspace ID (or 'private' for private notes)
  // and the value is a map of task IDs to frequencies
  const frequencyMapsReference = useRef<Map<string, Map<string, Frequency>>>(
    new Map()
  )

  // Initialize with a map for private notes
  if (!frequencyMapsReference.current.has('private')) {
    frequencyMapsReference.current.set('private', new Map<string, Frequency>())
  }

  /**
   * Get the frequency map for a specific context (private or teamspace)
   *
   * This utility function handles getting or creating the appropriate frequency map
   * based on the context (teamspace ID or 'private' for private notes).
   *
   * @param teamspaceId - The teamspace ID, or undefined for private context
   * @returns The map key used to access the frequency map
   */
  const getFrequencyMapForContext = (teamspaceId = 'private'): string => {
    // If the map doesn't exist yet, create it
    if (!frequencyMapsReference.current.has(teamspaceId)) {
      frequencyMapsReference.current.set(
        teamspaceId,
        new Map<string, Frequency>()
      )
    }

    return teamspaceId
  }

  /**
   * Process notes for a specific context (private or teamspace) to infer frequencies
   *
   * @param sortedEntries - Sorted array of [dateKey, notes] entries
   * @param contextId - The context ID ('private' or teamspace ID)
   * @param taskInstancesByContext - Map to store task instances by context
   */
  const processNotesForFrequency = (
    sortedEntries: [string, Note[]][],
    contextId: string,
    taskInstancesByContext: Map<string, Map<string, Map<string, number>>>
  ) => {
    // Get the task instances map for this context
    const taskInstancesMap = taskInstancesByContext.get(contextId)
    if (!taskInstancesMap) {
      // This shouldn't happen as we initialize the map earlier, but just in case
      return
    }

    // First pass: collect all instances of repeat tasks
    for (const [dateKey, notes] of sortedEntries) {
      for (const note of notes) {
        if (!note.content) continue

        // Scan for repeat tags
        const lines = note.content.split('\n')
        for (const line of lines) {
          const match = REPEAT_TASK_REGEX.exec(line)
          if (match) {
            const taskBase = match[2]
            const repeatNumber = Number.parseInt(match[3], 10)
            const totalRepeats = Number.parseInt(match[4], 10)

            // Create unique task identifier
            const taskId = createTaskIdentifier(taskBase, totalRepeats)

            // Store this instance
            if (!taskInstancesMap.has(taskId)) {
              taskInstancesMap.set(taskId, new Map<string, number>())
            }

            const instancesForTask = taskInstancesMap.get(taskId)
            if (instancesForTask) {
              instancesForTask.set(dateKey, repeatNumber)
            }
          }
        }
      }
    }

    // Second pass: analyze task instances to infer frequencies
    for (const [taskId, instances] of taskInstancesMap.entries()) {
      if (instances.size < 2) continue

      // Sort instances by date key
      const sortedInstances = [...instances.entries()].sort((a, b) =>
        a[0].localeCompare(b[0])
      )

      // Look for sequential instances to infer frequency
      for (let index = 0; index < sortedInstances.length - 1; index++) {
        const currentInstance = sortedInstances[index]
        const nextInstance = sortedInstances[index + 1]

        if (currentInstance[1] + 1 === nextInstance[1]) {
          const dateKey1 = currentInstance[0]
          const date1 = dayjs(dateKey1)
          const date2 = dayjs(nextInstance[0])

          if (date1.isValid() && date2.isValid()) {
            const diff = date2.diff(date1, 'day')
            const frequency = inferFrequencyFromDayDiff(diff)

            // Get or create the frequency map for this context
            let contextMap = frequencyMapsReference.current.get(contextId)
            if (!contextMap) {
              contextMap = new Map<string, Frequency>()
              frequencyMapsReference.current.set(contextId, contextMap)
            }

            // Update the frequency map
            contextMap.set(taskId, frequency)
            break
          }
        }
      }
    }
  }
  // Access to the note processor for adding/removing content in notes
  const noteProcessor = useNoteProcessor()
  // Access to all calendar notes by date for scanning existing repeat tasks
  const { data: privateCalendarNotesByDate } = usePrivateCalendarNotesByDate()
  const { data: teamCalendarNotesByDate } = useTeamCalendarNotesByDate()
  // Access to the currently selected date in the UI
  const selectedDate = useSelectedDate()
  // Access to the currently selected record name (note identifier)
  const selectedRecordName = useSelectedRecordName()

  /**
   * Check for deleted repeat tasks and handle their cleanup
   *
   * This function is called when a note's content changes. It detects if any repeating
   * tasks have been deleted and, if confirmed by the user, removes all future instances
   * of those tasks from their respective calendar notes.
   *
   * The function:
   * 1. Determines the reference date based on the note's timeframe
   * 2. Identifies deleted repeat tasks by comparing old and new content
   * 3. Asks for user confirmation before deleting future instances
   * 4. Calculates future dates for each deleted task based on its frequency
   * 5. Batches removal operations by date and sends them to the note processor
   *
   * @param oldContent - The previous content of the note
   * @param newContent - The current content of the note
   * @param filename - The filename of the note (used to determine the date/timeframe)
   */
  const checkForDeletedRepeatTasks = useCallback(
    (oldContent: string, newContent: string, filename: string) => {
      if (oldContent === newContent) return

      // Extract date information from the filename
      const { date: extractedDate, timeframe } = filenameToDate(filename)

      // Determine the correct reference date based on the timeframe
      // Default to today's date for non-calendar notes or month/quarter/year timeframes
      let currentDate = dayjs()

      // If we have a valid date from the filename (it's a calendar note)
      if (extractedDate) {
        if (timeframe === 'week') {
          // For week timeframe, use the start of the week
          currentDate = extractedDate.startOf('week')
        } else if (timeframe === 'day') {
          // For day timeframe, use the extracted date as is
          currentDate = extractedDate
        }
        // For month, quarter, year timeframes, we keep the default (today's date)
      }
      // If not a calendar note (extractedDate is null), we keep the default (today's date)

      // Find deleted repeat tasks
      const oldLines = oldContent.split('\n')
      const newLines = newContent.split('\n')

      const deletedRepeatTasks: {
        taskBase: string
        currentRepeatNumber: number
        totalRepeats: number
        frequency: Frequency
      }[] = []

      for (const line of oldLines) {
        const match = REPEAT_TASK_REGEX.exec(line)

        if (match) {
          const taskBase = match[2] // Includes the bullet and trailing whitespace
          const currentRepeatNumber = Number.parseInt(match[3], 10)
          const totalRepeats = Number.parseInt(match[4], 10)

          // Check if this repeat task still exists in the new content
          const taskExists = newLines.some((newLine) => {
            const newMatch = REPEAT_TASK_REGEX.exec(newLine)
            return (
              newMatch &&
              Number.parseInt(newMatch[3], 10) === currentRepeatNumber &&
              Number.parseInt(newMatch[4], 10) === totalRepeats
            )
          })

          // Only consider it deleted if it's completely gone (not just edited)
          if (!taskExists && currentRepeatNumber < totalRepeats) {
            // Create task identifier
            const taskId = createTaskIdentifier(taskBase, totalRepeats)

            // Get the frequency map for the current context
            const teamspaceId = selectedDate.teamspace
            const mapKey = getFrequencyMapForContext(teamspaceId)

            // Get frequency (default to daily if not found)
            const frequency =
              frequencyMapsReference.current.get(mapKey)?.get(taskId) ??
              FREQUENCY.DAY

            deletedRepeatTasks.push({
              taskBase,
              currentRepeatNumber,
              totalRepeats,
              frequency,
            })
          }
        }
      }

      if (deletedRepeatTasks.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('oldContent', oldContent)
        // eslint-disable-next-line no-console
        console.debug('newContent', newContent)
        // eslint-disable-next-line no-console
        console.debug('deletedRepeatTasks', deletedRepeatTasks)

        // Ask the user if they want to delete the repeat task
        const confirm = window.confirm(
          `This is a repeating todo. Delete future todos too?`
        )
        if (!confirm) return
      }

      // Find and remove future instances for each deleted task
      const allItemsByDate = new Map<
        string,
        { dateString: string; pattern: string; type: 'remove' }[]
      >()

      for (const {
        taskBase,
        currentRepeatNumber,
        totalRepeats,
        frequency,
      } of deletedRepeatTasks) {
        const itemsByDate = new Map<
          string,
          { dateString: string; pattern: string; type: 'remove' }[]
        >()

        // Calculate future dates based on frequency
        for (let index = currentRepeatNumber; index < totalRepeats; index++) {
          const nextRepeatNumber = index + 1

          const futureDate = calculateNextDate(
            currentDate,
            nextRepeatNumber - currentRepeatNumber,
            frequency
          )
          const dateString = dayjs(futureDate).format('YYYYMMDD')
          if (!itemsByDate.has(dateString)) {
            itemsByDate.set(dateString, [])
          }
          itemsByDate.get(dateString)?.push({
            dateString,
            pattern: `${taskBase}@repeat(${nextRepeatNumber.toString()}/${totalRepeats.toString()})`,
            type: 'remove',
          })
        }

        // Merge into allItemsByDate
        for (const [dateString, items] of itemsByDate.entries()) {
          if (!allItemsByDate.has(dateString)) {
            allItemsByDate.set(dateString, [])
          }
          allItemsByDate.get(dateString)?.push(...items)
        }
      }

      // Convert to a flat list of remove operations
      const itemsToRemove: RemoveContentInput[] = []
      for (const [dateString, items] of allItemsByDate.entries()) {
        const patterns = items.map((item) => item.pattern)
        itemsToRemove.push({
          dateString,
          pattern: patterns.length === 1 ? patterns[0] : patterns,
          type: 'remove',
        })
      }

      // Process the cleanup
      if (itemsToRemove.length > 0) {
        // Pass the teamspace ID directly to ensure it doesn't change if selectedDate changes
        const teamspaceId = selectedDate.teamspace
        noteProcessor.removeContent(
          itemsToRemove,
          'Remove repeating tasks',
          teamspaceId
        )
      }
    },
    [noteProcessor, selectedDate.teamspace]
  )

  /**
   * Handle repeat task creation events
   *
   * This function is triggered when a user creates a new repeating task via the UI.
   * It receives the task details from a custom event and creates future instances
   * of the task in the appropriate calendar notes.
   *
   * The function:
   * 1. Records the frequency of the task in the frequency map for future reference
   * 2. Determines the correct reference date based on the current context
   * 3. Calculates future dates for each repeat based on the frequency
   * 4. Creates add operations for each future instance
   * 5. Sends the batch of add operations to the note processor
   *
   * The reference date logic is important and varies based on the note type:
   * - For day notes: Use the selected date
   * - For week notes: Use the start of the selected week
   * - For month/quarter/year notes: Use today's date
   * - For non-calendar notes: Use today's date
   *
   * @param originalTask - The text content of the task (including the bullet)
   * @param times - The total number of times the task should repeat
   * @param frequency - How often the task repeats (day, week, month, quarter, year)
   * @param batchId - A unique identifier for this batch of operations
   */
  const handleRepeatTask = useCallback(
    ({ originalTask, times, frequency, batchId }: RepeatTaskEventDetail) => {
      if (times <= 1) return

      // Create task identifier and update frequency map
      const taskId = createTaskIdentifier(originalTask, times)

      // Get the frequency map for the current context
      const teamspaceId = selectedDate.teamspace
      const mapKey = getFrequencyMapForContext(teamspaceId)

      // Update the frequency map
      frequencyMapsReference.current.get(mapKey)?.set(taskId, frequency)

      // Generate dates based on frequency
      const addOperations: AddContentInput[] = []

      // Determine the correct reference date based on the selected date context
      // Default to selected date, but we'll override in specific cases
      // For non-calendar notes, use today's date
      // For month/quarter/year notes, use today's date
      // For week notes, use the start of the week
      // For day notes, keep the selected date
      let currentDate = selectedDate.date

      // Check if the selected record name indicates a calendar note
      const isCalendarNote = [
        'daily',
        'weekly',
        'monthly',
        'quarterly',
        'yearly',
      ].some((prefix) => selectedRecordName.startsWith(prefix))

      // Use today's date for non-calendar notes and month/quarter/year timeframes
      if (
        !isCalendarNote ||
        selectedDate.activePeriod === 'month' ||
        selectedDate.activePeriod === 'quarter' ||
        selectedDate.activePeriod === 'year'
      ) {
        currentDate = dayjs()
      } else if (selectedDate.activePeriod === 'week') {
        // For week notes, use the start of the week
        currentDate = dayjs()
          .year(selectedDate.year)
          .week(selectedDate.week)
          .startOf('week')
      }

      // Generate future dates for repeats
      for (let index = 1; index < times; index++) {
        const nextRepeatNumber = index + 1 // Start with 2/N for the first repeat
        const futureDate = calculateNextDate(currentDate, index, frequency)
        const dateString = futureDate.format('YYYYMMDD')
        const content = `${originalTask} @repeat(${nextRepeatNumber.toString()}/${times.toString()})`

        addOperations.push({
          dateString,
          content,
          type: 'add',
        })
      }

      // Process if we have items
      if (addOperations.length > 0) {
        // Pass the teamspace ID directly to ensure it doesn't change if selectedDate changes
        const teamspaceId = selectedDate.teamspace
        noteProcessor.addContent(
          addOperations,
          'Add repeating tasks',
          batchId,
          teamspaceId
        )
      }
    },
    [noteProcessor, selectedDate, selectedRecordName]
  )

  /**
   * Effect to scan existing notes and build the initial frequency map
   *
   * This effect runs when the calendar notes data is available. It scans all calendar
   * notes to find existing repeating tasks and analyzes their patterns to infer
   * their frequencies.
   *
   * The process works in two passes:
   * 1. First pass: Collect all instances of repeat tasks across all notes
   * 2. Second pass: Analyze sequential instances to infer their frequency
   *
   * For each task, it looks for pairs of consecutive instances (e.g., @repeat(1/5) and @repeat(2/5))
   * and calculates the day difference between them to determine the frequency.
   *
   * This allows the system to correctly handle deletion of repeating tasks even if
   * it wasn't the system that created them initially and also after reloading.
   */
  useEffect(() => {
    if (!privateCalendarNotesByDate && !teamCalendarNotesByDate) return

    // Map to track task instances across all notes, separated by context
    // The outer map key is the context ID ('private' or teamspace ID)
    // The inner map is the task ID to its instances
    const taskInstancesByContext = new Map<
      string,
      Map<string, Map<string, number>>
    >()

    // Initialize the map for private notes
    taskInstancesByContext.set(
      'private',
      new Map<string, Map<string, number>>()
    )

    // Process private calendar notes
    if (privateCalendarNotesByDate) {
      // Create a sorted array of entries for chronological processing
      const privateSortedEntries = [
        ...privateCalendarNotesByDate.entries(),
      ].sort((a, b) => a[0].localeCompare(b[0]))

      // Process private notes
      processNotesForFrequency(
        privateSortedEntries,
        'private',
        taskInstancesByContext
      )
    }

    // Process team calendar notes - each team space separately
    if (teamCalendarNotesByDate) {
      // Group notes by team space
      const notesByTeamspace = new Map<string, Map<string, Note[]>>()

      // First, organize notes by teamspace
      for (const [dateKey, notes] of teamCalendarNotesByDate.entries()) {
        for (const note of notes) {
          if (note.parent) {
            const teamspaceId = note.parent

            // Initialize maps if needed
            if (!notesByTeamspace.has(teamspaceId)) {
              notesByTeamspace.set(teamspaceId, new Map<string, Note[]>())
            }

            const teamspaceNotes = notesByTeamspace.get(teamspaceId)
            if (teamspaceNotes) {
              if (!teamspaceNotes.has(dateKey)) {
                teamspaceNotes.set(dateKey, [])
              }

              // Add the note to the appropriate teamspace and date
              const dateNotes = teamspaceNotes.get(dateKey)
              if (dateNotes) {
                dateNotes.push(note)
              }
            }
          }
        }
      }

      // Now process each teamspace separately
      for (const [teamspaceId, teamNotes] of notesByTeamspace.entries()) {
        // Initialize the map for this teamspace
        taskInstancesByContext.set(
          teamspaceId,
          new Map<string, Map<string, number>>()
        )

        // Create a sorted array of entries for this teamspace
        const teamSortedEntries = [...teamNotes.entries()].sort((a, b) =>
          a[0].localeCompare(b[0])
        )

        // Process this teamspace's notes
        processNotesForFrequency(
          teamSortedEntries,
          teamspaceId,
          taskInstancesByContext
        )
      }
    }
  }, [privateCalendarNotesByDate, teamCalendarNotesByDate])

  /**
   * Effect to set up event listener for repeat task events
   *
   * This effect sets up a global event listener for the custom 'noteplan:repeatTask' event,
   * which is dispatched by the UI components when a user creates a repeating task.
   *
   * When such an event is received, it validates the event data and calls the handleRepeatTask
   * function to process the repeating task creation.
   *
   * Using a custom event allows for loose coupling between the UI components (in the BlockNote
   * editor) and this provider, which handles the actual task creation logic.
   */
  useEffect(() => {
    const handleEvent = (event: Event) => {
      // Validate that the event is a CustomEvent with the expected properties
      if (
        event instanceof CustomEvent &&
        'originalTask' in event.detail &&
        'times' in event.detail &&
        'frequency' in event.detail &&
        'batchId' in event.detail
      ) {
        const detail = event.detail as RepeatTaskEventDetail
        handleRepeatTask(detail)
      }
    }

    // Add the event listener when the component mounts
    document.addEventListener('noteplan:repeatTask', handleEvent)

    // Remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('noteplan:repeatTask', handleEvent)
    }
  }, [handleRepeatTask])

  return (
    <RepeatTaskContext.Provider value={{ checkForDeletedRepeatTasks }}>
      {children}
    </RepeatTaskContext.Provider>
  )
}
